
import { defineComponent, PropType, nextTick } from 'vue';
import { mapActions, mapState } from 'pinia'
import { required, requiredIf } from '@white-label-helper/vuelidate';
import type VueI18n from 'vue-i18n';
import type { Time } from '@white-label-types/time-picker';
import type {
  AirlineVisibilityRules,
  Airport,
  BaggageType,
  BaggageVisibilityRules,
  LoungeFormData,
  SearchCriteriaConfig,
  Terminal
} from '@white-label-types/search-box';
import type { CustomersOptions, CustomersValues } from '@white-label-types/customers';

import {
  add,
  DATE_TIME_FORMATS,
  formatWithLocale,
  guessTimezone,
  isBefore,
  utcToZonedTime,
  startOfToday,
  parseISO
} from '@white-label-helper/date-utilities';
import { convertTimeStringToObject } from '@white-label-helper/time-helpers';
import {
  APP_HERO_PRODUCTS,
  PRODUCT_TYPE_NAMES,
  BAGGAGE_SELECTOR_OPTIONS,
  HeroProduct,
  LOUNGES_TIMEPICKER_STEPS,
  ProductType,
  PRODUCT_TYPES,
  ROUTE_NAMES,
  NAMED_ROUTES,
  FEATURE_FLAGS,
} from '@white-label-configuration/constants';
import { getAppVariable } from '@white-label-helper/get-app-variable';
import { gaDataLayerLounges } from '@white-label-helper/ga-data-layer';
import { getAppHeroProduct } from '@white-label-helper/get-app-hero-product';
import { useMultiBasketStore } from '@white-label-store/multi-basket';

import DateTimePicker from '../date-time-picker/date-time-picker.vue';
import { DropdownWrapper, IconMessage, DotsLoader, Backdrop } from 'ui-shared-components';
import CustomersSelector from '../customers-selector/customers-selector.vue';
import SearchBoxExtrasProductInput from '../search-box-extras-product-input/search-box-extras-product-input.vue';
import { trackAction } from '@white-label-helper/shared-helpers';
import { useManageBookingStore } from '@white-label-store/manage-booking';
import { closeModal, popstateEventHandler, pushModalState } from '@white-label-helper/helper-history-modal';
import { getFeatureFlag } from '@white-label-plugin/launch-darkly';

interface DateTimePickerHTMLElement extends HTMLElement {
  $el: HTMLElement;
  dateDropdownOpened?: boolean;
  timeDropdownOpened?: boolean;
}

export default defineComponent({
  name: 'SearchBoxLounges',
  components: {
    Backdrop,
    IconCheck: () => import('@white-label-icon/icon-check'),
    IconUser: () => import('@white-label-icon/icon-user'),
    IconPencil: () => import('@white-label-icon/icon-pencil'),
    IconMessage,
    IconTerminal: () => import('@white-label-icon/icon-terminal'),
    IconSwap: () => import('@white-label-icon/icon-swap'),
    DropdownWrapper,
    DateTimePicker,
    CustomersSelector,
    SearchBoxExtrasProductInput,
    DotsLoader
  },
  props: {
    submitLabel: {
      type: String as PropType<string>,
      default: 'searchBox.buttonLabel',
    },
    disabledParams: {
      type: Object as PropType<{ [key: string]: any }>,
      required: false,
      default() {
        return {
          airport: '',
          entryDate: '',
          entryTime: {
            name: '',
            value: '',
          },
          exitDate: '',
          exitTime: null,
          terminal: '',
          airline: '',
          customers: {
            adult: 0,
            child: 0,
            infant: 0,
            senior: 0,
            children_ages:[]
          },
          discount: '',
          swapped: false,
          baggage_type: '',
        } as { [key: string]: any };
      },
    },
    customErrorMessages: {
      type: Object,
      required: false,
      default: () => {},
    },
    apiRequestRunning: {
      type: Boolean,
      default: false,
    },
    enableSwap: {
      type: Boolean,
      default: false,
    },
    enableExtrasProductInput: {
      type: Boolean,
      default: false,
    },
    isEditable: {
      type: Boolean,
      default: true,
    },
    showAgeRanges: {
      type: Boolean,
      default: true,
    },
    modalVersion: {
      type: Boolean,
      default: false,
    },
    searchPage: {
      type: Boolean,
      default: false,
    },
    minEntryDate: {
      type: [String, Date] as PropType<string | Date>,
      default: '',
    },
    searchCriteriaDefaultConfig: {
      type: Object as PropType<SearchCriteriaConfig>,
      default: null
    },
    noDateFields: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const isPrivateTerminal =
      getAppHeroProduct('parking') === APP_HERO_PRODUCTS.LOUNGES &&
      getAppVariable('products.lounges.airlines_list', []).length > 0;

    return {
      removePopstateHandlerCustomers: null,
      removePopstateHandlerTerminal: null,
      customersData: {
        adult: 1,
        child: 0,
        senior: 0,
        infant: 0,
      },
      customersError: '',
      terminals: [] as Terminal[],
      searchCriteriaConfig: {} as SearchCriteriaConfig,
      primaryLanguage: '',
      airlines: [],
      airlinesVisibilityRules: {} as AirlineVisibilityRules,
      baggageVisibilityRules: {} as BaggageVisibilityRules,
      LOUNGES_TIMEPICKER_STEPS,
      formData: {
        airport: '',
        entryDate: '',
        entryTime: null,
        exitDate: '',
        exitTime: null,
        terminal: '',
        airline: '',
        customers: {} as CustomersValues,
        discount: '',
        swapped: false,
        baggage_type: '',
        selectedExtrasProductType: '',
      } as unknown as LoungeFormData & { swapped: boolean, selectedExtrasProductType: ProductType|string },
      exitDateOpened: false,
      terminalOpened: false,
      airlineOpened: false,
      customersOpened: false,
      entryTimeHaveChanged: false,
      airportOpened: false,
      isEntryDateTimePickerOpen: false,
      isExitDateTimePickerOpen: false,
      baggageOpened: false,
      isLoungesProduct: getAppHeroProduct('parking') === 'lounges',
    };
  },

  validations(): object {
    const validationObject = {
      formData: {
        airport: {},
        entryDate: { required },
        entryTime: { required },
        exitTime: {},
        exitDate: {},
        terminal: {},
        airline: { required: requiredIf(() => this.showAirlinesSelector) },
        baggage_type: { required: requiredIf(() => this.showBaggageSelector) },
        customers: {
          validation: (value: {
            child: number;
            infant: number;
            adult: number;
            senior: number;
            children_ages: number[];
          }) => {
            this.customersError = '';
            if (!this.isCustomerGroupsExists) return true;
            if (!Object.values(value).some((item) => item > 0)) {
              this.customersError = 'Please select number of guests';
              return false;
            }

            if (this.isChildAgeConfigurationAvailable && value.child > 0 && (value.children_ages?.length || 0) < value.child) {
              this.customersError = this.$t('guestDetails.ageIsRequired');
              return false;
            }

            if ((parseInt(value.senior, 10) || 0) + (parseInt(value.adult, 10) || 0) === 0) {
              this.customersError = 'At least 1 adult required';
              return false;
            }
            return true;
          },
        },
      },
    };
    // at least one date-time pair required
    if (!this.hasOneDatePicker) {
      validationObject.formData.exitDate = {
        required: requiredIf(
          () =>
            (!this.formData.entryDate && !this.formData.entryTime?.value) ||
            this.formData.exitTime?.value
        ),
      };
      validationObject.formData.exitTime = {
        required: requiredIf(
          () =>
            (!this.formData.entryDate && !this.formData.entryTime?.value) ||
            this.formData.exitDate
        ),
      };
      validationObject.formData.entryDate = {
        required: requiredIf(
          () =>
            (!this.formData.exitDate && !this.formData.exitTime?.value) ||
            this.formData.entryTime?.value
        ),
      };
      validationObject.formData.entryTime = {
        required: requiredIf(
          () =>
            (!this.formData.exitDate && !this.formData.exitTime?.value) ||
            this.formData.entryDate
        ),
      };
    }

    if (this.isMultiAirportPartner) {
      validationObject.formData.airport = { required };
      validationObject.formData.terminal = this.isMultiAirportTerminalsExist
        ? { required }
        : {};
    } else if (this.isTerminalsExists) {
      validationObject.formData.terminal = { required };
    }
    return validationObject;
  },
  computed: {
    ...mapState(useManageBookingStore, ['readAmendItem']),
    ...mapState(useMultiBasketStore, {
      readContinueClicked: 'readContinueClicked'
    }),
    customersBackdrop: {
      get(): boolean {
        return this.isMobileResolution && this.customersOpened;
      },
      set(): void {
        this.customersOpened = false;
      },
    },
    privateTerminalMinPickerDate(): string | Date {
      let minBookingDate = new Date();
      if (this.isPrivateTerminal) {
        let privateTerminalMinBookingDate = new Date(2024, 10, 4);

        if (isBefore(minBookingDate, privateTerminalMinBookingDate)) {
          minBookingDate = privateTerminalMinBookingDate;
        }
      }
      return minBookingDate;
    },
    maxDatePickerDate(): string | Date {
      return add(this.privateTerminalMinPickerDate, { years: 2 });
    },
    minDate(): string | Date {
      if (startOfToday().getTime() === startOfToday(this.currentTimeInPartnerTimeZone).getTime()) {
        //if date is today, then the time should be limited to the time slot 2 segments ahead of 1 hour from now.
        return add(this.currentTimeInPartnerTimeZone, { minutes: 75 });
      }

      return utcToZonedTime(this.currentTimeInPartnerTimeZone, this.partnerTimeZone);
    },
    currentTimeInPartnerTimeZone(): string {
      return utcToZonedTime(new Date(), this.partnerTimeZone);
    },
    getSearchCriteriaSerialized(): string {
      return JSON.parse(localStorage.getItem('searchCriteriaSerialized') ?? '{}');
    },
    amendItem(): ReturnType<typeof readAmendItem> {
      return this.readAmendItem;
    },
    productType(): ProductType {
      let productType: ProductType = this.defaultHeroProduct;

      if (
        this.enableExtrasProductInput
          && this.extrasProductTypes?.includes(this.selectedExtrasProductType)
      ) {
        productType = this.selectedExtrasProductType;
      }

      if (this.amendItem) {
        productType = this.amendItem?.product_code ?? productType;
      }

      return productType;
    },
    selectedExtrasProductType: {
      get(): ProductType {
          let formValue = this.formData.selectedExtrasProductType;
          if (formValue === '') {
            if (this.isExtrasProductRoutePath && this.extrasProductTypes?.includes(PRODUCT_TYPES.EXTRAS)) {
              formValue = PRODUCT_TYPES.EXTRAS;
            } else {
              formValue = this.extrasProductTypes[0];
            }
          }
          return formValue as ProductType;
      },
      set(newValue: ProductType): void {
        this.formData.selectedExtrasProductType = newValue;
      }
    },
    selectedExtrasProductTypeName() {
      return PRODUCT_TYPE_NAMES[this.selectedExtrasProductType?.toUpperCase()];
    },
    isExtrasProductRoutePath(): boolean {
      return this.$route?.path?.includes(NAMED_ROUTES.select_extras.product);
    },
    extrasProductTypes(): ProductType[] {
      return this.availableProductTypes.filter(
        (product: ProductType) => product !== this.defaultHeroProduct
      );
    },
    availableProductTypes(): array {
      return getAppVariable(`product_types_available`) || [this.defaultHeroProduct];
    },
    crossSellMixedResultsFlagEnabled(): boolean {
      return this.$launchDarkly.variation('EM-369-CROSS-SELL-MIXED-RESULTS');
    },

    customerGroups(): CustomersOptions|null {
      let customerGroups;
      customerGroups = getAppVariable(`products.${this.productType}.customer_groups`) as CustomersOptions;
      if (this.isTravelExtrasPage && this.crossSellMixedResultsFlagEnabled) {
        customerGroups = getAppVariable(`products.lounges.customer_groups`)
        || getAppVariable(`products.fasttrack.customer_groups`) as CustomersOptions;
      }

      if (!customerGroups) {
        return null;
      }

      const hasAdultGroup = 'adult' in customerGroups;
      const hasNoSeniorGroup = !('senior' in customerGroups);
      const noAdultCustomers = this.formData?.customers?.adult === 0;

      const returnDefaultAdult = hasAdultGroup && hasNoSeniorGroup && noAdultCustomers

      if (returnDefaultAdult) {
        this.customersData.adult = 1;
      }

      return customerGroups;
    },
    isPrivateTerminal(): boolean {
      return (
        this.productType === APP_HERO_PRODUCTS.LOUNGES
          && getAppVariable('products.lounges.airlines_list', []).length > 0
      );
    },
    minPickerDate(): string | Date {
      return this.isPrivateTerminal && !this.enabledBookingDateLimitations
        ? this.privateTerminalMinPickerDate
        : this.minEntryDate || this.minDate;
    },
    showCompactFilterVersion(): boolean {
      return (
        this.enabledAetherImprovements &&
        [
          this.showMultiAirportsDropdown,
          this.showMultiAirportTerminalSelect,
          !this.isMultiAirportPartner && this.isTerminalsExists,
          !this.hasOneDatePicker,
          this.isCustomerGroupsExists,
          this.showAirlinesSelector,
          this.showBaggageSelector,
        ].filter((v) => v).length >= 4 &&
        !this.modalVersion &&
        this.searchPage &&
        !this.isMobileResolution &&
        !this.isTabletResolution
      );
    },
    submitFromOutside() {
      return (
        this.readContinueClicked &&
        this.$route.name === ROUTE_NAMES.home
      );
    },
    isSearchPage(): boolean {
      return this.$route?.name === ROUTE_NAMES.search || false;
    },
    discountFromQuery(): string {
      return this.$route?.query?.discount as string;
    },

    partnerTimeZone(): string {
      return getAppVariable('poi.timezone') || guessTimezone();
    },

    terminalsWithDefault(): Terminal[] {
      return [
        ...this.terminals,
        {
          name: this.$t('searchBox.dontKnowOption'),
          id: 'dontKnow',
        },
      ];
    },

    baggageTypes(): BaggageType[] {
      return Object.keys(BAGGAGE_SELECTOR_OPTIONS).map((option) => ({
        id: option,
        name: this.$t(`components.baggageSelector.${option}`) as string,
      }));
    },

    isDisabledSearch(): boolean {
      /** Due to how we store the disabledParams we need to match the
       *  object structure to the formData object structure then compare
       */
      const currentBookingCriteria = { ...this.formData, terminal: this.formData.terminal?.toString()};
      const previousSearchCriteria = { ...this.disabledParams, terminal: this.disabledParams.terminal?.toString() }; ;
      for (const customerKey in currentBookingCriteria.customers) {
        if (!(customerKey in previousSearchCriteria.customers)) {
          if (currentBookingCriteria?.customers[customerKey] === 0) {
            delete currentBookingCriteria?.customers[customerKey];
          }
        }
      }

      if (currentBookingCriteria.entryTime) {
        delete currentBookingCriteria.entryTime?.$isDisabled;
        delete currentBookingCriteria.exitTime?.$isDisabled;
      }

      return (
        JSON.stringify(previousSearchCriteria) ===
        JSON.stringify(currentBookingCriteria)
      );
    },

    computedTerminal: {
      get(): Terminal {
        return (
          this.terminalsWithDefault.find(
            (i) => i.id.toString() === this.formData.terminal?.toString()
          ) || {
            name: '',
            id: '',
          }
        );
      },

      set(val: Terminal): void {
        this.formData.terminal = val.id.toString();
      },
    },

    computedAirlines: {
      get() {
        return (
          this.airlines.find(
            (i) => i.iata.toString() === this.formData.airline?.toString()
          ) || {
            iata: '',
            name: '',
          }
        );
      },

      set(val: Terminal): void {
        this.formData.airline = val.iata.toString();
      },
    },
    computedBaggageTypes: {
      get() {
        return (
          (this.baggageTypes?.length &&
            this.baggageTypes.find(
              (i: BaggageType) => i.id === this.formData.baggage_type
            )) || {
            id: '',
            name: '',
          }
        );
      },
      set(val: BaggageType) {
        this.formData.baggage_type = val.id;
      },
    },
    computedCustomers(): { name: string; id: string } {
      const { adult, senior, infant, child } = this.customersData;
      const label = [];

      const getAgeText = (key: 'senior' | 'adult' | 'child' | 'infant', count: number) => {
        return (this.isSearchPage ? 'x ' : '') + this.$tc(`shared.customers.${key}`, count);
      };

      if (senior) {
        label.push(`${senior} ${getAgeText('senior', senior)}`);
      }
      if (adult) {
        label.push(`${adult} ${getAgeText('adult', adult)}`);
      }
      if (child) {
        label.push(`${child} ${getAgeText('child', child)}`);
      }
      if (infant) {
        label.push(`${infant} ${getAgeText('infant', infant)}`);
      }

      Object.assign(this.formData, { customers: { ...this.customersData } });
      return label.length
        ? { id: 'id', name: label.join(', ') }
        : {
            name: '',
            id: '',
          };
    },
    guestsNumber(): number {
      const { adult, senior, infant, child } = this.customersData;
      return (adult || 0) + (senior || 0) + (infant || 0) + (child || 0);
    },

    isSearchConfigExists() {
      return (
        this.searchCriteriaConfig &&
        Object.keys(this.searchCriteriaConfig).length > 0
      );
    },

    isTravelExtrasPage() {
      return this.$route.name === ROUTE_NAMES.travel_extras;
    },

    hasOneDatePicker() {
      if (!this.isSearchConfigExists) {
        return true;
      }
      return this.searchCriteriaConfig.picker_type === 'one';
    },

    isTerminalsExists(): boolean {
      return this.terminals?.length > 1;
    },

    isCustomerGroupsExists(): boolean {
      return Boolean(
        this.customerGroups && Object.keys(this.customerGroups).length > 0
      );
    },

    allDateTimeFieldsFilled(): boolean {
      const { ...rest } = this.formData;
      return !Object.values(rest).some((item) => !item);
    },

    terminalError(): boolean {
      // @ts-ignore
      return !!this.$v.formData?.terminal?.$error;
    },

    airlineError(): boolean {
      // @ts-ignore
      return !!this.$v.formData?.airline?.$error;
    },

    baggageError(): boolean {
      // @ts-ignore
      return !!this.$v.formData?.baggage_type?.$error;
    },

    entryDateError(): boolean {
      if (this.apiRequestRunning) return false;
      if (
        !this.customErrorMessages?.entryDate?.length &&
        !this.$v.formData?.entryDate?.$error
      )
        return false;
      if (this.customErrorMessages?.entryDate) {
        if (this.entryTimeHaveChanged) return false;
        return true;
      }
      return !!this.$v.formData?.entryDate?.$error;
    },

    entryTimeError(): boolean {
      if (this.apiRequestRunning) return false;
      if (
        !this.customErrorMessages?.entryTime?.length &&
        !this.$v.formData?.entryTime?.$error
      )
        return false;
      // @ts-ignore

      if (this.customErrorMessages?.entryTime) {
        if (this.entryTimeHaveChanged) return false;
        return true;
      }
      // @ts-ignore
      return !!this.$v.formData?.entryTime?.$error;
    },

    entryDateTimeError(): VueI18n.TranslateResult {
      if (this.apiRequestRunning) return '';
      if (this.customErrorMessages?.entryDate.length) {
        return this.customErrorMessages.entryDate;
      }
      if (this.customErrorMessages?.entryTime.length) {
        return this.customErrorMessages.entryTime;
      }
      if (this.entryDateError && this.entryTimeError) {
        return this.$t('validationError.entryDateTime.dateTimeError');
      }
      if (this.entryDateError) {
        return this.$t('validationError.entryDateTime.dateError');
      }
      if (this.entryTimeError) {
        return this.$t('validationError.entryDateTime.timeError');
      }
      return '';
    },

    exitDateError(): boolean {
      // @ts-ignore
      return !!this.$v.formData?.exitDate?.$error;
    },

    exitTimeError(): boolean {
      // @ts-ignore
      return !!this.$v.formData?.exitTime?.$error;
    },

    exitDateTimeError(): VueI18n.TranslateResult {
      if (this.exitDateError && this.exitTimeError) {
        return this.$t('validationError.exitDateTime.dateTimeError');
      }
      if (this.exitDateError) {
        return this.$t('validationError.exitDateTime.dateError');
      }
      if (this.exitTimeError) {
        return this.$t('validationError.exitDateTime.timeError');
      }
      return '';
    },

    formDataKey(): string {
      return JSON.stringify(this.formData);
    },

    // Multi Airport start
    airports(): Airport[] {
      return getAppVariable('pois');
    },

    isMultiAirportPartner(): boolean {
      return this.airports?.length > 1;
    },

    airportError(): string {
      // @ts-ignore
      return this.$v.formData?.airport?.$error;
    },

    computedAirport: {
      get(): Airport {
        return (
          (this.airports?.length &&
            this.airports.find((i) => {
              if (this.formData && this.formData.airport) {
                return i.code.toString() === this.formData.airport.toString();
              }
              return false;
            })) || {
            name: '',
            id: '',
          }
        );
      },
      set(val: Airport): void {
        this.formData.airport = val.code.toString();
        if (!this.multiAirportTerminalsWithDefault.find((item) => item.id === this.computedMultiAirportTerminal.id)) {
          this.formData.terminal = '';
        }
      },
    },

    multiAirportTerminals(): Terminal[] {
      const allSelectedTerminals: Terminal[] = getAppVariable('terminals');
      const airportTerminals = this.airports.find(
        (item) => item.code === this.formData.airport
      )?.terminals;

      if (airportTerminals) {
        return airportTerminals.filter((t1) =>
          allSelectedTerminals.some((t2) => t1.id === t2.id)
        );
      } else {
        return [];
      }
    },

    isMultiAirportTerminalsExist(): boolean {
      return this.multiAirportTerminals?.length;
    },

    showMultiAirportTerminalSelect(): boolean {
      return (
        this.isMultiAirportPartner &&
        this.formData.airport &&
        this.isMultiAirportTerminalsExist
      );
    },

    multiAirportTerminalsWithDefault(): Terminal[] {
      return [
        ...this.multiAirportTerminals,
        {
          name: this.$t('searchBox.dontKnowOption'),
          id: 'dontKnow',
        },
      ];
    },

    computedMultiAirportTerminal: {
      get(): Terminal {
        return (
          this.multiAirportTerminalsWithDefault.find(
            (i) => i.id.toString() === this.formData.terminal?.toString()
          ) || {
            name: '',
            id: '',
          }
        );
      },
      set(val: Terminal): void {
        this.formData.terminal = val.id.toString();
      },
    },

    isAllowedToShownPages() {
      return ['/', '/travel-extras'].includes(this.$nuxt.context.route.path);
    },

    airportSelectReadonly() {
      return ['/travel-extras'].includes(this.$nuxt.context.route.path);
    },

    showMultiAirportsDropdown() {
      return this.isMultiAirportPartner && this.isAllowedToShownPages;
    },
    // Multi Airport end

    showAirlinesSelector(): boolean {
      return (
        this.airlinesVisibilityRules?.is_enabled_in_landing_page &&
        this.airlines &&
        this.airlines.length > 0
      );
    },

    showBaggageSelector(): boolean {
      return this.baggageVisibilityRules?.is_enabled_in_landing_page;
    },
    defaultHeroProduct: (): HeroProduct => {
      return getAppHeroProduct('parking');
    },
    isCustomersEmpty() {
      return (
        Object.keys(this.formData.customers).reduce(
          (a, key) => a + this.formData.customers[key],
          0
        ) === 0
      );
    },
    enabledAetherImprovements() {
      return this.$launchDarkly?.variation(FEATURE_FLAGS.PREM_636_AETHER_NEW_SEARCH_FORM);
    },
    enabledBookingDateLimitations() {
      return this.$launchDarkly?.variation(
        FEATURE_FLAGS.PREM_764_AETHER_OVERRIDE_DATE_LIMITATIONS
      );
    },
    disableCloseClickingOutsideGuestsDropdown(): boolean {
      return getFeatureFlag('CRO_938_DYNAMIC_GUEST_UPDATE') && !this.isMobileResolution;
    },
    isChildAgeConfigurationAvailable(){
      return Boolean(this.customerGroups?.child_age_configuration);
    },
  },

  watch: {
    formDataKey() {
      this.$emit('formUpdate');
    },
    computedCustomers() {
      if (!getFeatureFlag('CRO_938_DYNAMIC_GUEST_UPDATE') || this.isMobileResolution) {
        this.customersOpened = false;
      }
    },
    submitFromOutside() {
      this.submitForm();
    },
    computedAirport() {
      if (!this.isMultiAirportTerminalsExist) {
        this.formData.terminal = 'dontKnow';
      }
    },
    customerGroups(value) {
      this.$emit('set-customer-groups', value);
    },
    terminalOpened: {
      handler(value) {
        if(!getFeatureFlag('CRO_887_BACK-MODALS-FUNCTIONALITY')){
          return;
        }
        if (value) {
          pushModalState('terminalOpened');
        } else {
          closeModal();
        }
      }
    },
    customersOpened: {
      handler(value) {
        if(!getFeatureFlag('CRO_887_BACK-MODALS-FUNCTIONALITY')){
          return;
        }
        if (value) {
          pushModalState('customersOpened');
        } else {
          closeModal();
        }
      }
    },
    isDisabledSearch: {
      handler(isDisabled) {
        this.$emit('button-state-change', !isDisabled);
      },
      immediate: true
    }
  },

  created() {
    this.terminals = getAppVariable('terminals');
    this.airlines = getAppVariable('products.lounges.airlines_list');
    this.airlinesVisibilityRules = getAppVariable('airlines_visibility_rules');
    this.searchCriteriaConfig = this.searchCriteriaDefaultConfig ? this.searchCriteriaDefaultConfig : getAppVariable(
      'search_criteria',
      {}
    ) as SearchCriteriaConfig;
    this.baggageVisibilityRules = getAppVariable('baggage_visibility_rules');
    this.primaryLanguage = getAppVariable('default_language');

    this.$emit('created');
  },

  mounted() {
    if (this.modalVersion) {
      this.setSearchCriteria(this.getSearchCriteriaSerialized);
    }
    this.$emit('mounted');
    if (this.crossSellMixedResultsFlagEnabled && this.isTravelExtrasPage && this.isGuestsInputEmpty()) {
      this.setDefaultGuestsToOneAdult();
    }
    if (getFeatureFlag('CRO_887_BACK-MODALS-FUNCTIONALITY')) {
      this.removePopstateHandlerCustomers = popstateEventHandler('customersOpened', ()=>this.customersOpened = false);
      this.removePopstateHandlerTerminal = popstateEventHandler('terminalOpened', () => this.terminalOpened = false);
      this.customersOpened = window.location.hash?.includes('customersOpened');
      this.terminalOpened = window.location.hash?.includes('terminalOpened');
    }
  },

  beforeDestroy() {
    this.commitContinueClicked(false);
    if (getFeatureFlag('CRO_887_BACK-MODALS-FUNCTIONALITY')) {
      if (this.removePopstateHandlerCustomers) {
        this.removePopstateHandlerCustomers();
      }
      if (this.removePopstateHandlerTerminal) {
        this.removePopstateHandlerTerminal();
      }
    }
  },

  methods: {
    ...mapActions(useMultiBasketStore, {
      commitContinueClicked: 'commitContinueClicked',
    }),

    /** onDropdownListChange is to handle the business case
     *  of automatically opening the next empty input
     *  it requires
     *  `$event` for tracking purposes (we can extend in the future)
     *  `target` this is to open the next empty dropdown
     *
     * optional
     *  `source`: so we can fire tracking
     */
    onDropdownListChange($event: Event, target: string, source?: string): void {
      if(this.formData[target]) return

      if (source === 'selectTerminal') {
        this.searchBoxTrackAction('Search widget', {
            event: JSON.stringify($event),
        }, "terminal_selector")
      }

      if (target === 'selectCustomers') {
        const isCustomersDefault = (this.formData.customers.adult || 1) === 1 &&
            (this.formData.customers.child || 0) === 0 &&
            (this.formData.customers.infant || 0) === 0 &&
            (this.formData.customers.senior || 0) === 0 &&
            (this.formData.customers.children_ages || []).length === 0;

        if (
            Object.keys(this.formData.customers).length !== 0 &&
            !isCustomersDefault
        ) {
          return;
        }
      }
      if (target === 'selectBaggage' && this.formData.baggage_type) return
      if (target === 'selectTerminal') {
        this.terminalOpened = true;
      }

      this.focusOnElement(target);
    },

    onFocusEvent(focusedElement: string): void {
      this.toggleDropdown(focusedElement);
      this.$emit('focus-on-element', focusedElement);
    },

    onBlurEvent() {
      this.$emit('blur');
    },

    focusOnElement(target: string) {
      const elementToFocus = this.$refs[target];
      if (!elementToFocus) return;

      this.toggleDropdown(target, true);


      if(elementToFocus.$el?.scrollIntoView && target === 'exitDate') {
        elementToFocus.$el.scrollIntoView({ behavior: 'smooth' });
      }
    },

    toggleDropdown(target: string, value?:boolean ): void {
      const dropdowns: Record<string, (shouldOpen?: boolean) => void> = {
        entryDate: (shouldOpen = false) => {
          this.isEntryDateTimePickerOpen = shouldOpen;
          this.$refs.entryDate && (this.$refs.entryDate.dateDropdownOpened = shouldOpen);
        },
        exitDate: (shouldOpen = false) => {
          this.isExitDateTimePickerOpen = shouldOpen;
          if (this.$refs.exitDate) {
            this.$refs.exitDate.dateDropdownOpened = shouldOpen;
          }
          },
        selectTerminal: (shouldOpen = false) => (this.terminalOpened = shouldOpen),
        selectCustomers: (shouldOpen = false) => (this.customersOpened = shouldOpen),
        selectAirline: (shouldOpen = false) => (this.airlineOpened = shouldOpen),
        selectBaggage: (shouldOpen = false) => (this.baggageOpened = shouldOpen),
        submitButton: () => {
          if(target === 'selectBaggage') return
          this.baggageOpened = false
        },
      };

      if (Object.hasOwn(dropdowns, target)) {
        // close all other dropdowns when a different input in focus
        const filteredDropdowns = Object.keys(dropdowns).filter(
          (key) => key !== target
        );

        if (
          getFeatureFlag('CRO_938_DYNAMIC_GUEST_UPDATE') &&
          !this.isMobileResolution &&
          this.customersOpened &&
          target === 'submitButton' &&
          this.$refs.customersSelector
        ) {
          if (!this.$refs.customersSelector.isCustomerChildSelectorsValid()) {
            return;
          }
          this.$refs.customersSelector.apply();
        }

        filteredDropdowns.forEach((dropdownToClose) => {
          dropdowns[dropdownToClose]();
        });
      }

      if (value) {
        dropdowns[target](value);
      }
    },

    formattedDate(date: string): string {
      return date
        ? formatWithLocale(
            parseISO(date),
            DATE_TIME_FORMATS.month_day_year,
            this.$i18n.locale
          )
        : '';
    },
    onCustomersSelectorChange($event: { [key: string]: any }) {
      this.customersOpened = false;
      this.searchBoxTrackAction('Search widget', {
          event: JSON.stringify($event),
      }, "guests_selector");

      this.$nextTick(() => {
        //added for accessibility to keep the focus on the dropdown wrapper (with customers selection) when It's closed
        const customersWrapperInstance = this.$refs.selectCustomers as InstanceType<typeof DropdownWrapper>;
        if (customersWrapperInstance && customersWrapperInstance.$refs.dropdownWrapper) {
          const focusableElement = customersWrapperInstance.$refs.dropdownWrapper as HTMLElement;
          focusableElement.focus();
        }
      });

      if(!this.formData.airline && this.$refs.selectAirline) {
        this.focusOnElement('selectAirline');
      }
    },

    onCustomersUpdated($event: any) {
      if (!getFeatureFlag('CRO_938_DYNAMIC_GUEST_UPDATE') || this.isMobileResolution) {
        return;
      }

      this.customersData = $event;
    },

    searchBoxTrackAction(
      actionName: string,
      context:  Record<string, string | number | undefined | LoungeFormData>,
      contextKey: string
    ) {
      const trackingSharedContext = {
        productType: this.defaultHeroProduct,
        productFlow: this.defaultHeroProduct,
      };

      trackAction(actionName, { [contextKey]: {...trackingSharedContext, ...context }});
    },

    pushGTM() {
      const extendTrackingInformation = {
        loginStatus: this.$auth.isAuthenticated || false,
        userId: this.$auth.user?.sub || '',
      }
      this.$gtm.push(gaDataLayerLounges(this.getSearchCriteria(), extendTrackingInformation));
    },

    focus() {
      if (this.$refs['entryDate']) {
        const datePicker = this.$refs['entryDate'] as DateTimePickerHTMLElement;
        if (!datePicker) return;
        datePicker.dateDropdownOpened = true;
        datePicker.$el.scrollIntoView({ behavior: 'smooth' });
      }
    },

    onEntryDateChange(date: string): void {
      this.formData.entryDate = date;
      this.entryTimeHaveChanged = true;
      const entryTimeIsNotAvailable =
        this.formData.entryTime &&
        isBefore(
          parseISO(`${date} ${this.formData.entryTime.value}`),
          new Date()
        );

      if (entryTimeIsNotAvailable) {
        this.formData.entryTime = null;
      }

      // Automatically open entryTime field if empty
      if (!this.formData.entryTime && this.$refs['entryDate']) {
        (this.$refs['entryDate'] as DateTimePickerHTMLElement).timeDropdownOpened =
          true;
      }


      this.searchBoxTrackAction('Search widget', {
          date: date,
          time: this.formData.entryTime?.value,
      }, "entry_date_selector");
    },

    onEntryTimeChange(time: Time | null): void {
      this.formData.entryTime = time;
      this.entryTimeHaveChanged = true;

      if (!this.formData.swapped && !this.formData.terminal) {
        this.focusOnElement(this.$refs.exitDate ? 'exitDate' : 'selectTerminal');
      } else if (!this.formData.terminal && this.$refs.selectTerminal) {
        this.terminalOpened = true;
      } else if (this.isCustomersEmpty) {
        this.customersOpened = true;
      }
    },

    onExitDateChange(date: string): void {
      this.formData.exitDate = date;
      this.entryTimeHaveChanged = true;
      const entryTimeIsNotAvailable =
        this.formData.exitTime &&
        isBefore(
          parseISO(`${date} ${this.formData.exitTime.value}`),
          new Date()
        );

      if (entryTimeIsNotAvailable) {
        this.formData.exitTime = null;
      }


      if (!this.formData.exitTime && this.$refs['exitDate']) {
        (this.$refs['exitDate'] as DateTimePickerHTMLElement).timeDropdownOpened =
          true;
      }


      this.searchBoxTrackAction('Search widget', {
          date: date,
          time: this.formData.exitTime?.value,
      }, "exit_date_selector");
    },

    onExitTimeChange(time: Time | null): void {
      this.formData.exitTime = time;
      this.entryTimeHaveChanged = true;
      if (!this.formData.terminal && this.$refs['selectTerminal']) {
        this.terminalOpened = true;
      } else if (this.formData.swapped) {
        this.focus();
      } else if (this.isCustomersEmpty) {
        this.customersOpened = true;
      }
    },

    submitForm(): void {
      if (getFeatureFlag('CRO_938_DYNAMIC_GUEST_UPDATE') && !this.isMobileResolution) {
        if (this.$refs.customersSelector) {
          this.$refs.customersSelector.apply();
        }
      }
      // @ts-ignore
      this.$v.formData.$touch();
      if (this.$v.formData.$invalid && getFeatureFlag('CRO_938_DYNAMIC_GUEST_UPDATE') && !this.isMobileResolution) {
        return;
      }

      this.$emit('submit', {
        // @ts-ignore
        error: this.$v.formData?.$error,
        form: this.formData,
        searchCriteria: this.getSearchCriteria(),
        searchCriteriaSerialized: this.getSearchCriteria(true),
      });
      this.entryTimeHaveChanged = false;
      this.searchBoxTrackAction('Search widget', {
        form: { ...this.formData },
      }, "search_cta_selected");

    },
    getSearchCriteria(serialize = false): {
      [key: string]: any;
      groups?: { [key: string]: any } | string;
    } {
      const result: { [key: string]: any } = {
        date: this.formData?.entryDate || '',
        time: this.formData?.entryTime?.value || '',
        exitDate: this.formData?.exitDate || '',
        exitTime: this.formData?.exitTime?.value || '',
        terminal: `${this.formData.terminal || ''}`,
        airline: `${this.formData.airline || ''}`,
        swapped: this.formData.swapped || false,
        children_ages: [],
        groups: {} as { [key: string]: any },
      };

      if (this.formData.airport) {
        result.airport = this.formData.airport;
      }

      if (!this.hasOneDatePicker) {
        result['date2'] = this.formData.exitDate || '';
        result['time2'] = this.formData.exitTime?.value || '';
      }

      if (this.formData.baggage_type) {
        result['baggage_type'] = this.formData.baggage_type;
      }

      const { customers } = this.formData;
      Object.keys(customers)
        .filter((key) => customers[key])
        .forEach((key: string) => {
          if(key !== 'children_ages') {
            result.groups[key] = customers[key];
          } else {
            result[key] = customers[key];
          }
        });
      if (serialize) {
        result.groups = JSON.stringify(result.groups);
      }
      if (this.discountFromQuery) {
        result.discount = this.discountFromQuery;
      }
      return result;
    },

    setSearchCriteria(data: { [key: string]: any }) {
      this.formData.entryDate = data.date || '';
      this.formData.exitDate = data.exitDate || '';
      this.formData.airport = data.airport || '';
      this.formData.terminal = data.terminal || '';
      this.formData.airline = data.airline || '';
      this.formData.swapped = data.swapped && data.swapped === 'true';
      this.formData.discount = data.discount || '';
      this.formData.baggage_type = data.baggage_type || '';
      this.formData.entryTime = data.time
        ? convertTimeStringToObject(data.time)
        : null;
      this.formData.exitTime = data.exitTime
        ? convertTimeStringToObject(data.exitTime)
        : null;
      const customers = data.groups || data.customers || {};
      this.customersData =
        typeof customers === 'string'
          ? JSON.parse(customers)
          : { ...customers };

      if(data.children_ages) {
        if(Array.isArray(data.children_ages)){
          this.formData.children_ages = data.children_ages
          this.customersData.children_ages = data.children_ages
        } else if (typeof data.children_ages === 'string') {
          this.formData.children_ages = [data.children_ages]
          this.customersData.children_ages = [data.children_ages]
        }
      }

      for(const key in this.customersData) {
        if (this.customersData.hasOwnProperty(key) && parseInt(this.customersData[key]) < 1 && !Array.isArray(this.customersData[key])) {
          delete this.customersData[key];
        }
      }

      if (!this.hasOneDatePicker) {
        this.formData.exitDate = data['date2'] || '';
        this.formData.exitTime = data['time2']
          ? convertTimeStringToObject(data['time2'])
          : null;
      }
    },

    updateCustomers(data) {
      this.customersData = data;
    },

    swapDatePickers() {
      this.formData.swapped = !this.formData.swapped;
    },

    openGuestSelector(): void {
      this.customersOpened = true;
    },

    getPickerLabel(labelKey: 'picker1_label' | 'picker2_label') {
      if (labelKey === 'picker1_label') {
        if (this.isSearchConfigExists) {
          return (
            this.searchCriteriaConfig.picker1_label?.[this.$i18n.locale] ||
            this.searchCriteriaConfig.picker1_label?.[this.primaryLanguage] ||
            this.$t('searchBox.entry')
          );
        }
        return this.$t('searchBox.entry');
      }
      return (
        this.searchCriteriaConfig.picker2_label?.[this.$i18n.locale] ||
        this.searchCriteriaConfig.picker2_label?.[this.primaryLanguage] ||
        ''
      );
    },

    getPickerDescription(descriptionKey: 'picker1_description' | 'picker2_description') {
      if (!this.isSearchConfigExists) {
        return '';
      }
      const description = this.searchCriteriaConfig[descriptionKey]?.[this.$i18n.locale]
        ?? this.searchCriteriaConfig[descriptionKey]?.[this.primaryLanguage];

      return description ?? '';
    },

    setDefaultGuestsToOneAdult() {
      this.customersData.adult = 1;
      this.customersData.child = 0;
      this.customersData.infant = 0;
      this.customersData.senior = 0;
    },

    isGuestsInputEmpty() {
      return Object.keys(this.customersData).reduce(
        (a, key) => a + this.customersData[key],
        0
      ) === 0;
    },

    outsideBackdropClicked() {
      if (!getFeatureFlag('CRO_938_DYNAMIC_GUEST_UPDATE') || this.isMobileResolution) {
        this.customersOpened = false;
        return;
      }

      if (this.$refs.customersSelector) {
        this.$refs.customersSelector.apply();
      }
    },

    // custom esc event emitter for accessibility, allows to disable backdrop on ESC press.
    onEscEvent() {
      this.$emit('esc-event')
    }
  },
});
